import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTheme } from '../contexts/ThemeContext';
import { FinancialAffidavitModal } from './FinancialAffidavitModal';
import { CaseData } from '../types/case';
import { CustomTooltip } from './CustomTooltip';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useAuthContext } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGem, faRobot } from '@/icons';
import { isJudicialCase } from '../utils/caseTeamType';

const ALIMONY_ANALYSIS_DOWNLOAD_OPTION = 'Alimony Analysis';
const ALLOCATION_ANALYSIS_DOWNLOAD_OPTION = 'Allocation Analysis';

interface CaseListDownloadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onDownload: (
        option: string,
        affidavitDate?: string | null,
        affidavitIsBlank?: boolean,
        affidavitSignatureBlockType?: 'notarySignatureBlock' | 'penaltyOfPerjury'
    ) => void;
    caseId?: string;
    stateCode?: string;
    disabled?: boolean;
    childSupportGuidelinesEnabled?: boolean;
    financialDocsEnabled?: boolean;
    caseData?: CaseData | null;
    isPro?: boolean;
    onHighIncomeEstimateProRequired?: () => void;
    onAlimonyAnalysisProRequired?: () => void;
    onCalculonAllocationAdviceProRequired?: () => void;
    onWordDownload?: (option: string, affidavitDate?: string | null, affidavitIsBlank?: boolean, affidavitSignatureBlockType?: 'notarySignatureBlock' | 'penaltyOfPerjury') => void;
    onWordDocProRequired?: () => void;
}

export const CaseListDownloadModal: React.FC<CaseListDownloadModalProps> = ({
    isOpen,
    onClose,
    onDownload,
    caseId,
    stateCode,
    disabled = false,
    caseData,
    childSupportGuidelinesEnabled,
    financialDocsEnabled,
    isPro = true,
    onHighIncomeEstimateProRequired,
    onAlimonyAnalysisProRequired,
    onCalculonAllocationAdviceProRequired,
    onWordDownload,
    onWordDocProRequired,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const highIncomePdfEnabled = useFeatureFlag('HighIncomePdf');
    // Alimony Analysis: Iowa 2026 only, because that is the only template that exists -- the
    // server would refuse any other year, so the row explains instead of offering the click.
    const alimonyEnabled = useFeatureFlag('Alimony');
    const alimonyTaxYear = caseData?.basic?.taxYear ?? 0;
    const alimonyStateCode = caseData?.basic?.stateCode ?? 'IA';
    const alimonyYearSupported = alimonyTaxYear === 2026 && alimonyStateCode.toUpperCase() === 'IA';
    // ProDowngrade inertness: a non-Pro team's stored scenarios (left by a former Pro
    // subscription) must not change what it sees. For it the row always offers the upgrade.
    const alimonyHasScenario = useMemo(
        () => !isPro || (caseData?.alimonyAnalysis?.scenarios ?? []).length > 0,
        [caseData?.alimonyAnalysis?.scenarios, isPro],
    );
    const alimonyRowActive = !disabled && alimonyYearSupported && alimonyHasScenario;
    const alimonyCanDownload = alimonyRowActive && isPro;
    const alimonyTooltipText = useMemo(() => {
        if (!alimonyYearSupported) return `Alimony Analysis is not available for ${alimonyStateCode} ${alimonyTaxYear}.`;
        if (!alimonyHasScenario) return 'Add at least one scenario on the Alimony step first.';
        if (!isPro) return 'Alimony Analysis is a Pro feature. Upgrade to download it.';
        return 'Choose scenarios and download the alimony analysis (PDF).';
    }, [alimonyYearSupported, alimonyStateCode, alimonyTaxYear, alimonyHasScenario, isPro]);

    const calculonAllocationAdviceEnabled = useFeatureFlag('CalculonAllocationAdvice');
    const wordDocSupportFlagEnabled = useFeatureFlag('WordDocumentSupport');
    const { user } = useAuthContext();
    const wordDocSupportEnabled = wordDocSupportFlagEnabled && (user?.businessRole || '').toLowerCase() === 'judge';
    const isJudicial = isJudicialCase(caseData);
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const [hoveredWordOption, setHoveredWordOption] = useState<string | null>(null);
    const [pendingWordDoc, setPendingWordDoc] = useState(false);
    const [showFinancialAffidavitModal, setShowFinancialAffidavitModal] = useState(false);
    const mouseDownOutsideRef = useRef<boolean>(false);

    const worksheet1Errors = React.useMemo(() => {
        if (!caseData) return [];
        return caseData.validation?.childSupportDocs?.errors ?? [];
    }, [caseData]);

    const qualifiesHighIncome = !!caseData?.documents?.highIncomeEstimator;
    const minorChildrenInvolved = caseData?.basic?.minorChildrenInvolved === true;
    const highIncomeRowActive = React.useMemo(() => {
        if (!caseData) return false;
        return minorChildrenInvolved && qualifiesHighIncome && worksheet1Errors.length === 0 && !disabled;
    }, [caseData, minorChildrenInvolved, qualifiesHighIncome, worksheet1Errors.length, disabled]);

    const highIncomeCanDownload = highIncomeRowActive && isPro;

    const highIncomeTooltipText = React.useMemo(() => {
        if (!caseData) {
            return 'Loading case data…';
        }
        if (!minorChildrenInvolved) {
            return 'High Income Estimate applies only when minor children are involved in the case.';
        }
        if (worksheet1Errors.length > 0) {
            return 'Fix child support worksheet errors before downloading this PDF.';
        }
        if (!qualifiesHighIncome) {
            return 'Combined monthly income is at or below the guideline cap. High-income estimator data is not present for this case.';
        }
        if (highIncomeRowActive && !isPro) {
            return 'Upgrade to Pro to download the High Income Estimate PDF.';
        }
        return '';
    }, [caseData, minorChildrenInvolved, worksheet1Errors.length, qualifiesHighIncome, highIncomeRowActive, isPro]);

    const calculonRowActive = calculonAllocationAdviceEnabled && !disabled;
    const calculonCanDownload = calculonRowActive && isPro;

    const calculonTooltipText = React.useMemo(() => {
        if (!caseData) {
            return 'Loading case data…';
        }
        if (calculonRowActive && !isPro) {
            return 'Upgrade to Pro to download the Allocation Analysis PDF.';
        }
        return '';
    }, [caseData, calculonRowActive, isPro]);

    const initialSignatureBlockType = React.useMemo<'notarySignatureBlock' | 'penaltyOfPerjury' | null>(() => {
        const type = caseData?.basic?.affidavit?.type;
        if (type === 'notarySignatureBlock') return 'notarySignatureBlock';
        if (type === 'penaltyOfPerjury') return 'penaltyOfPerjury';
        if (type === 'notary_signature_block') return 'notarySignatureBlock';
        if (type === 'penalty_of_perjury') return 'penaltyOfPerjury';
        return null;
    }, [caseData?.basic?.affidavit?.type]);

    const WordDocButton = ({ option, enabled }: { option: string; enabled: boolean }) => {
        if (!wordDocSupportEnabled) return null;
        const canClick = enabled && !disabled;
        const tooltipText = !isPro
            ? 'Upgrade to Pro to download Word documents'
            : 'Download as Word document (.docx)';
        return (
            <div style={{ position: 'absolute', right: theme.spacing.md, top: 0, bottom: 0, display: 'flex', alignItems: 'center', zIndex: 1 }}>
                <CustomTooltip
                    show={hoveredWordOption === option}
                    text={canClick ? tooltipText : ''}
                    position="right"
                    portal
                >
                    <button
                        style={{
                            background: hoveredWordOption === option && canClick ? theme.colors.primary : 'none',
                            border: `1px solid ${canClick && isPro ? (hoveredWordOption === option ? theme.colors.primary : hoveredItem === getItemKey(option) ? theme.colors.text.inverse : theme.colors.border.default) : 'transparent'}`,
                            borderRadius: theme.borderRadius.sm,
                            cursor: canClick ? 'pointer' : 'not-allowed',
                            opacity: canClick ? 1 : 0.4,
                            color: hoveredWordOption === option && canClick ? theme.colors.text.inverse : hoveredItem === getItemKey(option) ? theme.colors.text.inverse : 'currentColor',
                            fontSize: theme.typography.fontSize.xs,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '2px 6px',
                            whiteSpace: 'nowrap',
                            transition: 'background 0.1s, color 0.1s',
                        }}
                        onMouseEnter={() => setHoveredWordOption(option)}
                        onMouseLeave={(e) => { setHoveredWordOption(null); (e.currentTarget as HTMLButtonElement).style.background = ''; (e.currentTarget as HTMLButtonElement).style.filter = ''; }}
                        onMouseDown={(e) => { if (canClick) (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(0.85)'; }}
                        onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.filter = ''; }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!canClick) return;
                            setHoveredWordOption(null);
                            if (!isPro) { onWordDocProRequired?.(); return; }
                            if (option === 'Financial Affidavit') {
                                setPendingWordDoc(true);
                                setShowFinancialAffidavitModal(true);
                                return;
                            }
                            onWordDownload?.(option);
                        }}
                    >
                        <FontAwesomeIcon icon={faGem} style={{ fontSize: theme.typography.fontSize.xs }} />
                        Word
                    </button>
                </CustomTooltip>
            </div>
        );
    };

    if (!isOpen) return null;

    const modalOverlay: React.CSSProperties = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
    };

    const modalContent: React.CSSProperties = {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: '0',
        width: '400px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        overflow: 'hidden',
        boxShadow: theme.shadows.lg,
        display: 'flex',
        flexDirection: 'column',
    };

    const modalHeader: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: `${theme.spacing.lg} ${theme.spacing.lg}`,
        borderBottom: `1px solid ${theme.colors.border.default}`,
        backgroundColor: theme.colors.primary,
    };

    const modalTitle: React.CSSProperties = {
        margin: 0,
        fontSize: theme.typography.fontSize.xl,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.text.inverse,
    };

    const modalCloseButton: React.CSSProperties = {
        background: 'none',
        border: 'none',
        fontSize: theme.typography.fontSize.xxl,
        color: theme.colors.text.inverse,
        cursor: 'pointer',
        padding: '0',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: theme.borderRadius.sm,
    };

    const modalBody: React.CSSProperties = {
        padding: theme.spacing.md,
        overflowY: 'auto',
        maxHeight: 'calc(80vh - 80px)',
    };

    const sectionHeader: React.CSSProperties = {
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        fontSize: theme.typography.fontSize.sm,
        color: theme.colors.text.muted,
        fontWeight: theme.typography.fontWeight.semibold,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: `1px solid ${theme.colors.border.default}`,
        backgroundColor: theme.colors.surfaceSecondary,
        whiteSpace: 'nowrap',
    };

    const dropdownItem: React.CSSProperties = {
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        fontSize: theme.typography.fontSize.sm,
        color: disabled ? theme.colors.text.muted : theme.colors.text.primary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: `background-color ${theme.transitions.normal}`,
        backgroundColor: 'transparent',
        opacity: disabled ? 0.5 : 1,
    };

    const dropdownItemHover: React.CSSProperties = {
        ...dropdownItem,
        color: theme.colors.text.inverse,
        backgroundColor: theme.colors.primary,
    };

    const getItemKey = (option: string): string => {
        return caseId ? `${caseId}-${option}` : option;
    };

    const handleItemClick = (option: string) => {
        if (disabled) return;
        
        // Show Financial Affidavit modal instead of directly downloading
        if (option === 'Financial Affidavit') {
            setShowFinancialAffidavitModal(true);
            return;
        }
        
        onDownload(option);
        // Don't close immediately - let the parent handle closing after download completes
    };

    const handleFinancialAffidavitDownload = (
        date: string | null,
        isBlank: boolean,
        signatureBlockType: 'notarySignatureBlock' | 'penaltyOfPerjury'
    ) => {
        setShowFinancialAffidavitModal(false);
        if (pendingWordDoc) {
            setPendingWordDoc(false);
            onWordDownload?.('Financial Affidavit', date, isBlank, signatureBlockType);
        } else {
            onDownload('Financial Affidavit', date, isBlank, signatureBlockType);
        }
    };

    const handleEditCase = () => {
        if (caseId) {
            navigate(`/wizard/${stateCode ?? 'IA'}/${caseId}/case`);
            onClose();
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        // Track if mousedown happened on the overlay (outside modal content)
        if (e.target === e.currentTarget && !disabled) {
            mouseDownOutsideRef.current = true;
        } else {
            mouseDownOutsideRef.current = false;
        }
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        // Only close if both mousedown and mouseup happened outside the modal
        if (e.target === e.currentTarget && !disabled && mouseDownOutsideRef.current) {
            onClose();
        }
        // Reset the flag
        mouseDownOutsideRef.current = false;
    };

    return (
        <div
            style={modalOverlay}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
        >
            <div style={modalContent} onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()}>
                <div style={modalHeader}>
                    <h2 style={modalTitle}>Download Document</h2>
                    <button
                        style={{
                            ...modalCloseButton,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.5 : 1,
                        }}
                        onClick={onClose}
                        disabled={disabled}
                        onMouseEnter={(e) => {
                            if (!disabled) {
                                e.currentTarget.style.opacity = '0.7';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = disabled ? '0.5' : '1';
                        }}
                    >
                        ×
                    </button>
                </div>
                <div style={modalBody}>
                    {disabled && (
                        <div style={{
                            padding: theme.spacing.md,
                            textAlign: 'center',
                            color: theme.colors.text.muted,
                            fontSize: theme.typography.fontSize.sm,
                            fontStyle: 'italic'
                        }}>
                            Generating PDF...
                        </div>
                    )}
                    {/* Child Support Guidelines Section */}
                    <div style={sectionHeader}>
                        Child Support Guidelines
                    </div>
                    {childSupportGuidelinesEnabled === true ? (
                        <>
                            <div style={{ position: 'relative' }}>
                                <CustomTooltip
                                    show={hoveredItem === getItemKey('Summary') && !disabled}
                                    position="right"
                                    portal
                                    text="Download the child support summary PDF."
                                    maxWidth={320}
                                >
                                    <div
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            paddingRight: wordDocSupportEnabled ? '72px' : undefined,
                                            ...(hoveredItem === getItemKey('Summary') && !disabled ? dropdownItemHover : dropdownItem),
                                            cursor: disabled ? 'not-allowed' : 'pointer',
                                            opacity: disabled ? 0.5 : 1,
                                        }}
                                        onClick={() => { if (disabled) return; handleItemClick('Summary'); }}
                                        onMouseEnter={() => { if (!disabled) setHoveredItem(getItemKey('Summary')); }}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        Summary
                                    </div>
                                </CustomTooltip>
                                <WordDocButton option="Summary" enabled={!disabled} />
                            </div>
                            <div style={{ position: 'relative' }}>
                                <CustomTooltip
                                    show={hoveredItem === getItemKey('Worksheet 1') && !disabled}
                                    position="right"
                                    portal
                                    text="Download Iowa Child Support Guideline Worksheet 1 (PDF)."
                                    maxWidth={320}
                                >
                                    <div
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            paddingRight: wordDocSupportEnabled ? '72px' : undefined,
                                            ...(hoveredItem === getItemKey('Worksheet 1') && !disabled ? dropdownItemHover : dropdownItem),
                                            cursor: disabled ? 'not-allowed' : 'pointer',
                                            opacity: disabled ? 0.5 : 1,
                                        }}
                                        onClick={() => { if (disabled) return; handleItemClick('Worksheet 1'); }}
                                        onMouseEnter={() => { if (!disabled) setHoveredItem(getItemKey('Worksheet 1')); }}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        Worksheet 1
                                    </div>
                                </CustomTooltip>
                                <WordDocButton option="Worksheet 1" enabled={!disabled} />
                            </div>
                            <div style={{ position: 'relative' }}>
                                <CustomTooltip
                                    show={hoveredItem === getItemKey('Summary + Worksheet 1') && !disabled}
                                    position="right"
                                    portal
                                    text="Download the Summary and Worksheet 1 together in one PDF."
                                    maxWidth={320}
                                >
                                    <div
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            paddingRight: wordDocSupportEnabled ? '72px' : undefined,
                                            ...(hoveredItem === getItemKey('Summary + Worksheet 1') && !disabled ? dropdownItemHover : dropdownItem),
                                            cursor: disabled ? 'not-allowed' : 'pointer',
                                            opacity: disabled ? 0.5 : 1,
                                        }}
                                        onClick={() => { if (disabled) return; handleItemClick('Summary + Worksheet 1'); }}
                                        onMouseEnter={() => { if (!disabled) setHoveredItem(getItemKey('Summary + Worksheet 1')); }}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        Summary + Worksheet 1
                                    </div>
                                </CustomTooltip>
                                <WordDocButton option="Summary + Worksheet 1" enabled={!disabled} />
                            </div>
                            {highIncomePdfEnabled && (
                                <div style={{ position: 'relative' }}>
                                    <CustomTooltip
                                        show={hoveredItem === getItemKey('High Income Estimate') && !disabled}
                                        position="right"
                                        portal
                                        text={
                                            highIncomeCanDownload
                                                ? 'Download the high-income extrapolation estimate (PDF).'
                                                : highIncomeTooltipText
                                        }
                                        maxWidth={320}
                                    >
                                        <div
                                            style={{
                                                display: 'block',
                                                width: '100%',
                                            }}
                                            onMouseEnter={() => {
                                                if (!disabled) {
                                                    setHoveredItem(getItemKey('High Income Estimate'));
                                                }
                                            }}
                                            onMouseLeave={() => setHoveredItem(null)}
                                        >
                                            <div
                                                style={{
                                                    ...(hoveredItem === getItemKey('High Income Estimate') && highIncomeRowActive ? dropdownItemHover : dropdownItem),
                                                    cursor: (disabled || !highIncomeRowActive) ? 'not-allowed' : 'pointer',
                                                    opacity: (disabled || !highIncomeRowActive) ? 0.5 : 1,
                                                    paddingRight: wordDocSupportEnabled ? '72px' : undefined,
                                                }}
                                                onClick={() => {
                                                    if (disabled || !highIncomeRowActive) return;
                                                    if (!isPro) {
                                                        onHighIncomeEstimateProRequired?.();
                                                        return;
                                                    }
                                                    handleItemClick('High Income Estimate');
                                                }}
                                            >
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                    <CustomTooltip text="Pro">
                                                        <FontAwesomeIcon
                                                            icon={faGem}
                                                            style={{ fontSize: theme.typography.fontSize.sm, color: 'currentColor' }}
                                                        />
                                                    </CustomTooltip>
                                                    High Income Estimate
                                                </span>
                                            </div>
                                        </div>
                                    </CustomTooltip>
                                    <WordDocButton option="High Income Estimate" enabled={highIncomeRowActive} />
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div style={{
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                textAlign: 'center',
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.muted,
                                marginBottom: theme.spacing.xs,
                            }}>
                                Case has validations that must be addressed before downloading
                            </div>
                            <div style={{
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                display: 'flex',
                                justifyContent: 'center',
                            }}>
                                <button
                                    onClick={handleEditCase}
                                    style={{
                                        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                        backgroundColor: theme.colors.primary,
                                        border: `1px solid ${theme.colors.primary}`,
                                        borderRadius: theme.borderRadius.sm,
                                        fontSize: theme.typography.fontSize.sm,
                                        fontWeight: theme.typography.fontWeight.medium,
                                        color: theme.colors.text.inverse,
                                        cursor: 'pointer',
                                        transition: `all ${theme.transitions.normal}`,
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = theme.colors.primaryHover || theme.colors.primary;
                                        e.currentTarget.style.borderColor = theme.colors.primaryHover || theme.colors.primary;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = theme.colors.primary;
                                        e.currentTarget.style.borderColor = theme.colors.primary;
                                    }}
                                >
                                    Edit Case
                                </button>
                            </div>
                        </>
                    )}

                    {/* Financial Section */}
                    <div style={sectionHeader}>
                        Financial
                    </div>
                    {financialDocsEnabled === true ? (
                        <>
                            <div style={{ position: 'relative' }}>
                                <CustomTooltip
                                    show={hoveredItem === getItemKey('Division Worksheet') && !disabled}
                                    position="right"
                                    portal
                                    text="Download the property division worksheet PDF."
                                    maxWidth={320}
                                >
                                    <div
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            paddingRight: wordDocSupportEnabled ? '72px' : undefined,
                                            ...(hoveredItem === getItemKey('Division Worksheet') && !disabled ? dropdownItemHover : dropdownItem),
                                            cursor: disabled ? 'not-allowed' : 'pointer',
                                            opacity: disabled ? 0.5 : 1,
                                        }}
                                        onClick={() => { if (disabled) return; handleItemClick('Division Worksheet'); }}
                                        onMouseEnter={() => { if (!disabled) setHoveredItem(getItemKey('Division Worksheet')); }}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        Division Worksheet
                                    </div>
                                </CustomTooltip>
                                <WordDocButton option="Division Worksheet" enabled={!disabled} />
                            </div>
                            {calculonAllocationAdviceEnabled && (
                                <CustomTooltip
                                    show={hoveredItem === getItemKey(ALLOCATION_ANALYSIS_DOWNLOAD_OPTION) && !disabled}
                                    position="right"
                                    portal
                                    text={
                                        calculonCanDownload
                                            ? 'Download Allocation Analysis (AI-generated PDF) for this case.'
                                            : calculonTooltipText
                                    }
                                    maxWidth={320}
                                >
                                    <div
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                        }}
                                        onMouseEnter={() => {
                                            if (!disabled) {
                                                setHoveredItem(getItemKey(ALLOCATION_ANALYSIS_DOWNLOAD_OPTION));
                                            }
                                        }}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        <div
                                            style={{
                                                ...(hoveredItem === getItemKey(ALLOCATION_ANALYSIS_DOWNLOAD_OPTION) && calculonRowActive
                                                    ? dropdownItemHover
                                                    : dropdownItem),
                                                cursor: disabled || !calculonRowActive ? 'not-allowed' : 'pointer',
                                                opacity: disabled || !calculonRowActive ? 0.5 : 1,
                                            }}
                                            onClick={() => {
                                                if (disabled || !calculonRowActive) return;
                                                if (!isPro) {
                                                    onCalculonAllocationAdviceProRequired?.();
                                                    return;
                                                }
                                                handleItemClick(ALLOCATION_ANALYSIS_DOWNLOAD_OPTION);
                                            }}
                                        >
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                <CustomTooltip text="Pro">
                                                    <FontAwesomeIcon
                                                        icon={faGem}
                                                        style={{ fontSize: theme.typography.fontSize.sm, color: 'currentColor' }}
                                                    />
                                                </CustomTooltip>
                                                <CustomTooltip text="AI-generated">
                                                    <FontAwesomeIcon
                                                        icon={faRobot}
                                                        style={{ fontSize: theme.typography.fontSize.sm, color: 'currentColor' }}
                                                    />
                                                </CustomTooltip>
                                                Allocation Analysis
                                            </span>
                                        </div>
                                    </div>
                                </CustomTooltip>
                            )}
                            {!isJudicial && <div style={{ position: 'relative' }}>
                                <CustomTooltip
                                    show={hoveredItem === getItemKey('Financial Affidavit') && !disabled}
                                    position="right"
                                    portal
                                    text="Open options to download the financial affidavit (dated or blank)."
                                    maxWidth={320}
                                >
                                    <div
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            paddingRight: wordDocSupportEnabled ? '72px' : undefined,
                                            ...(hoveredItem === getItemKey('Financial Affidavit') && !disabled ? dropdownItemHover : dropdownItem),
                                            cursor: disabled ? 'not-allowed' : 'pointer',
                                            opacity: disabled ? 0.5 : 1,
                                        }}
                                        onClick={() => {
                                            if (disabled) return;
                                            handleItemClick('Financial Affidavit');
                                        }}
                                        onMouseEnter={() => {
                                            if (!disabled) {
                                                setHoveredItem(getItemKey('Financial Affidavit'));
                                            }
                                        }}
                                        onMouseLeave={() => setHoveredItem(null)}
                                    >
                                        Financial Affidavit
                                    </div>
                                </CustomTooltip>
                                <WordDocButton option="Financial Affidavit" enabled={!disabled} />
                            </div>}
                            {alimonyEnabled && (
                                <div style={{ position: 'relative' }}>
                                    <CustomTooltip
                                        show={hoveredItem === getItemKey(ALIMONY_ANALYSIS_DOWNLOAD_OPTION) && !disabled}
                                        position="right"
                                        portal
                                        text={alimonyCanDownload
                                            ? 'Choose scenarios and download the alimony analysis (PDF).'
                                            : alimonyTooltipText}
                                        maxWidth={320}
                                    >
                                        <div
                                            style={{ display: 'block', width: '100%' }}
                                            onMouseEnter={() => { if (!disabled) setHoveredItem(getItemKey(ALIMONY_ANALYSIS_DOWNLOAD_OPTION)); }}
                                            onMouseLeave={() => setHoveredItem(null)}
                                        >
                                            <div
                                                style={{
                                                    ...(hoveredItem === getItemKey(ALIMONY_ANALYSIS_DOWNLOAD_OPTION) && alimonyRowActive ? dropdownItemHover : dropdownItem),
                                                    cursor: disabled || !alimonyRowActive ? 'not-allowed' : 'pointer',
                                                    opacity: disabled || !alimonyRowActive ? 0.5 : 1,
                                                }}
                                                onClick={() => {
                                                    if (disabled || !alimonyRowActive) return;
                                                    if (!isPro) { onAlimonyAnalysisProRequired?.(); return; }
                                                    handleItemClick(ALIMONY_ANALYSIS_DOWNLOAD_OPTION);
                                                }}
                                            >
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                    <CustomTooltip text="Pro">
                                                        <FontAwesomeIcon icon={faGem} style={{ fontSize: theme.typography.fontSize.sm, color: 'currentColor' }} />
                                                    </CustomTooltip>
                                                    {ALIMONY_ANALYSIS_DOWNLOAD_OPTION}
                                                </span>
                                            </div>
                                        </div>
                                    </CustomTooltip>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div style={{
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                textAlign: 'center',
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.muted,
                                marginBottom: theme.spacing.xs,
                            }}>
                                Case has validations that must be addressed before downloading
                            </div>
                            <div style={{
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                display: 'flex',
                                justifyContent: 'center',
                            }}>
                                <button
                                    onClick={handleEditCase}
                                    style={{
                                        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                        backgroundColor: theme.colors.primary,
                                        border: `1px solid ${theme.colors.primary}`,
                                        borderRadius: theme.borderRadius.sm,
                                        fontSize: theme.typography.fontSize.sm,
                                        fontWeight: theme.typography.fontWeight.medium,
                                        color: theme.colors.text.inverse,
                                        cursor: 'pointer',
                                        transition: `all ${theme.transitions.normal}`,
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = theme.colors.primaryHover || theme.colors.primary;
                                        e.currentTarget.style.borderColor = theme.colors.primaryHover || theme.colors.primary;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = theme.colors.primary;
                                        e.currentTarget.style.borderColor = theme.colors.primary;
                                    }}
                                >
                                    Edit Case
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
            
            {/* Financial Affidavit Modal */}
            <FinancialAffidavitModal
                isOpen={showFinancialAffidavitModal}
                onClose={() => setShowFinancialAffidavitModal(false)}
                onDownload={handleFinancialAffidavitDownload}
                initialSignatureBlockType={initialSignatureBlockType}
            />
        </div>
    );
};
